import { hideLessonFromContinueWatching } from '@/supabase/services/optimizedCourse';
import type { Lesson } from '@/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// Every query family whose data is the Lesson[] returned by
// fetchContinueLessonsForDashboard (dashboard Continue section, Continue
// "see all" page, purchased-courses page + hub hero card).
const CONTINUE_QUERY_PREFIXES: readonly (readonly string[])[] = [
  ['userLessons', 'continueForDashboard'],
  ['purchasedCourses', 'continue'],
  ['collection', 'continue'],
];

/**
 * "Remove from Continue watching" for one lesson: stamps
 * user_lesson_interactions.continue_hidden_at (via RPC), optimistically drops
 * the lesson from all cached Continue lists, then refetches them.
 * Watching the lesson again resurfaces it automatically.
 */
export function useHideLessonFromContinue(userId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lessonId: number) => {
      if (!userId) throw new Error('Not signed in');
      return hideLessonFromContinueWatching(lessonId);
    },
    onMutate: (lessonId: number) => {
      for (const prefix of CONTINUE_QUERY_PREFIXES) {
        queryClient.setQueriesData<Lesson[]>(
          { queryKey: prefix as unknown as string[] },
          (old) =>
            Array.isArray(old) ? old.filter((l) => l.id !== lessonId) : old,
        );
      }
    },
    onSettled: async () => {
      await Promise.all(
        CONTINUE_QUERY_PREFIXES.map((prefix) =>
          queryClient.invalidateQueries({
            queryKey: prefix as unknown as string[],
          }),
        ),
      );
    },
  });
}
